import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ReactQuill from "react-quill";
import Grid from "@mui/material/Grid";
import { TextField, Button, Box, Typography } from '@mui/material';
import { deepPurple } from '@mui/material/colors';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSnackbar } from "notistack";
import { ALLOWED_IMAGE_TYPE, ERRORS_MESSAGE, MAX_DOCUMENT_UPLOAD_SIZE, SUCCESS_MESSAGE } from '../../constants';
import { announcementPostSelector, clearAnnouncementPostState, getAnnouncementPostById, saveAnnouncementPostImage, updateAnnouncementPost, updateAnnouncementPostStatus } from '../../store/slices/announcementPostSlice';
import FileUploadDropzone from '../FileUpload/FileUploadDropzone';

const modules = {
  toolbar: {
    container: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline'],
      // ['image', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['clean'],
    ],
  },
};

const AnnouncementPost = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { announcementPostId } = useParams();
  const { announcementPost, isUpdatingAnnouncementPost, isAnnouncementPostStatusUpdating, isUploadingAnnouncementPostImage } = useSelector(announcementPostSelector);

  const [announcementData, setAnnouncementData] = useState({
    title: '',
    content: '',
    imageUrl: '',
  })

  const handle = {
    onChangeField: (field, value) => {
      setAnnouncementData({
        ...announcementData,
        [field]: value,
      });
    },
    getAnnouncementPostHandler: (announcementPostId) => {
      dispatch(getAnnouncementPostById({ announcementPostId }));
    },
    handleSave: () => {
      const payload = {
        announcementPostId,
        title: announcementData.title,
        content: announcementData.content,
      };

      dispatch(updateAnnouncementPost(payload)).then((res) => {
        if (res?.error) {
          enqueueSnackbar(res?.payload?.status_message || ERRORS_MESSAGE.dataUpdateError, {
            variant: "error",
            autoHideDuration: 5000,
          });
        } else {
          enqueueSnackbar(res?.payload?.data?.status_message || SUCCESS_MESSAGE.dataUpdate, {
            variant: "success",
            autoHideDuration: 5000,
          });
        }
      })
    },
    uploadPostImage: (files) => {
      const newSelectedFiles = [];
      const formData = new FormData();

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (ALLOWED_IMAGE_TYPE.includes(file.type)) {
          if (file.size <= MAX_DOCUMENT_UPLOAD_SIZE) {
            newSelectedFiles.push(file);
          }
        }
      }
      if (newSelectedFiles?.length) {
        newSelectedFiles.forEach((file) => {
          formData.append("file", file);
        });
        formData.append("announcementPostId", announcementPostId);

        dispatch(saveAnnouncementPostImage({ formData })).then((res) => {
          if (res?.error) {
            enqueueSnackbar(res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg, {
              variant: "error",
              autoHideDuration: 5000,
            });
          } else {
            enqueueSnackbar(res?.payload?.data?.status_message || SUCCESS_MESSAGE.fileUploadSuccess, {
              variant: "success",
              autoHideDuration: 5000,
            });
          }
        });
      }
    },
    setAnnouncementLive: (status) => {
      const payload = {
        announcementPostId,
        status
      }

      dispatch(updateAnnouncementPostStatus(payload)).then((res) => {
        if (res?.error) {
          enqueueSnackbar(res?.payload?.status_message || ERRORS_MESSAGE.dataUpdateError, {
            variant: "error",
            autoHideDuration: 5000,
          });
        } else {
          enqueueSnackbar(res?.payload?.data?.status_message || SUCCESS_MESSAGE.dataUpdate, {
            variant: "success",
            autoHideDuration: 5000,
          });
        }
      })
    },
    handleOnBack: () => {
      navigate(`/posts`);
    }
  }


  useEffect(() => {
    if (announcementPostId) {
      handle.getAnnouncementPostHandler(announcementPostId);
    }

    return () => {
      dispatch(clearAnnouncementPostState());
    };
  }, [announcementPostId]);

  useEffect(() => {
    if (announcementPost) {
      setAnnouncementData({
        title: announcementPost?.title || announcementData?.title,
        content: announcementPost?.content || announcementData?.content,
        imageUrl: announcementPost?.imageUrl || announcementData?.imageUrl,
      });
    }
  }, [announcementPost]);

  const announcementPostStatusText = announcementPost?.status !== "active" ? "Set Live" : "Set Inactive"

  return (
    <>
      <Grid
        container
        direction="column"
        style={{
          padding: "30px 0px 100px",
        }}
      >
        <Grid
          item
          px={4}
          style={{
            display: "flex",
            alignItems: "center",
            columnGap: "10px",
            justifyContent: "space-between",
          }}
        >
          <Grid
            item
            style={{
              display: "flex",
              alignItems: "center",
              columnGap: "10px",
            }}
          >
            <Button
              variant="text"
              sx={{ textTransform: "none", color: "black", ml: "-14px" }}
              onClick={handle.handleOnBack}
            >
              <ArrowBackIcon htmlColor={deepPurple[500]} />
            </Button>

            <Typography
              variant="h1"
              style={{
                fontSize: "24px",
                fontWeight: 800,
                letterSpacing: "-0.5px",
              }}
            >
              Announcement Post
            </Typography>
          </Grid>

          <Grid
            item
            style={{
              display: "flex",
              alignItems: "center",
              columnGap: "10px",
              marginTop: "10px"
            }}
          >
            <Button
              variant="contained"
              color="primary"
              disabled={isAnnouncementPostStatusUpdating}
              onClick={() => handle.setAnnouncementLive(announcementPost?.status !== "active" ? "active" : "inactive")}
            >
              {isAnnouncementPostStatusUpdating ? "Saving..." : announcementPostStatusText}
            </Button>

            <Button
              variant="contained"
              color="primary"
              disabled={isUpdatingAnnouncementPost}
              onClick={handle.handleSave}
            >
              {isUpdatingAnnouncementPost ? "Saving..." : "Save"}
            </Button>
          </Grid>
        </Grid>

        <Box px={4} py={1}>
          <TextField
            label="Title"
            fullWidth
            margin="normal"
            value={announcementData.title}
            onChange={(e) => handle.onChangeField("title", e.target.value)}
          />

          <ReactQuill
            value={announcementData.content || ""}
            modules={modules}
            onChange={(value) => {
              handle.onChangeField("content", value)
            }}
          />

          <FileUploadDropzone
            uploadPostImage={handle.uploadPostImage}
            isLoading={isUploadingAnnouncementPostImage}
            isMultiple={false}
            fileData={announcementData}
          />
        </Box>
      </Grid >
    </>
  );
}

export default AnnouncementPost